<!--
  InviteTeammatePage -- Distributor App Account / Team & Permissions.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Formalize team/invite as a sub-route (was a modal).

  Polish item 2026-06-04: the invite flow used to open as a v-dialog on
  the Team & Permissions page. The dialog's body and multi-step carousel
  (email entry -> cost confirm -> success) are unchanged; what changed is
  the host shell -- this page mounts at /distributor/account/team/invite
  and renders the existing InviteTeammateDialog (now opened persistently
  by an open(...) call on mount). The page itself owns the dirty-form
  detection + beforeRouteLeave guard mirrored on the campaign-editor
  pattern from PR #1329.

  Why a wrapper instead of refactoring the dialog body: the dialog and
  its carousel flow are tested, hooked into the subscription/Stripe
  checkout, and used elsewhere (DistributorAccountInfo). Wrapping
  preserves the live behaviour while adding the sub-route + leave guard.
-->
<template>
  <v-container fluid data-testid="dist-account-team-invite-page">
    <InviteTeammateDialog
      ref="inviteDialog"
      :teammates="teammates"
      :current-subscription-cost="currentSubscriptionCost"
      @invite-sent="onInviteSent"
    />

    <!--
      Unsaved-changes navigation guard. Mirrors the StoreAddEdit pattern
      (PR #1329): a plain in-tree overlay (not a v-dialog) so the router
      guard's deferred navigation is resolved by buttons that stay within
      this component's tree. Opened by beforeRouteLeave when the invite
      form has unsubmitted email entries.
    -->
    <div
      v-if="unsavedDialog"
      class="ip-unsaved-scrim"
      data-testid="invite-unsaved-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ip-unsaved-title"
    >
      <div class="ip-unsaved-card">
        <h2 id="ip-unsaved-title" class="ip-unsaved-title">
          Discard invitation?
        </h2>
        <p class="ip-unsaved-body">
          You have unsent invitations. Leave anyway and discard them, or
          stay to finish sending.
        </p>
        <div class="ip-unsaved-actions">
          <button
            type="button"
            class="ip-unsaved-btn ip-unsaved-btn--ghost"
            data-testid="invite-unsaved-stay"
            @click="cancelLeaveUnsaved"
          >
            Stay on page
          </button>
          <button
            type="button"
            class="ip-unsaved-btn ip-unsaved-btn--danger"
            data-testid="invite-unsaved-leave"
            @click="confirmLeaveUnsaved"
          >
            Discard and leave
          </button>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import InviteTeammateDialog from "./InviteTeammateDialog.vue";
import { useTeamSubscription } from "@/composables/useTeamSubscription";
import { useAccountStore } from "@/stores/account";
import axios from "@/axios";
import store from "@/store";

export default {
  name: "InviteTeammatePage",

  components: { InviteTeammateDialog },

  props: {
    // Optional explicit distributor id so the page is mount-testable
    // without a live store. Mirrors the TeamPermissionsPage signature.
    distributorId: {
      type: [Number, String],
      default: null,
    },
  },

  setup(props) {
    let resolvedId = props.distributorId;
    if (resolvedId === null || resolvedId === "") {
      try {
        const account = useAccountStore();
        resolvedId = account.distributor ? account.distributor.id : null;
      } catch (e) {
        resolvedId = null;
      }
    }
    const team = useTeamSubscription({
      axios,
      store,
      distributorId: resolvedId,
    });
    return { team };
  },

  data() {
    return {
      // Cached so the InviteTeammateDialog has the same prop shape it
      // saw under the legacy modal-on-the-team-page mount.
      teammates: [],
      currentSubscriptionCost: 0,
      unsavedDialog: false,
      // Stashed router `next` callback so the user's choice in the
      // unsaved-changes overlay resolves the deferred navigation.
      _pendingLeaveNext: null,
      // Set after the dialog emits `invite-sent` so beforeRouteLeave
      // does not double-prompt on the success-page close.
      inviteSent: false,
    };
  },

  computed: {
    // True when the invite form has any non-empty email entry that has
    // NOT yet been submitted to checkout. Reads the dialog's internal
    // `newTeammates` array via $refs; safe to call before mount (returns
    // false). Once the dialog reports `inviteSent`, the navigation is
    // intentional (the page is closing) and we suppress the guard.
    hasUnsavedInvitations() {
      if (this.inviteSent) return false;
      const ref = this.$refs.inviteDialog;
      if (!ref || !Array.isArray(ref.newTeammates)) return false;
      return ref.newTeammates.some(
        (entry) => typeof entry === "string" && entry.trim().length > 0
      );
    },
  },

  async mounted() {
    // Load teammate/subscription context so the dialog has the prop
    // shape it expects (existing-team-emails + current subscription
    // cost for the cost-confirm carousel page).
    try {
      await this.team.load();
      this.teammates = this.team.accounts.value || [];
      this.currentSubscriptionCost = this.team.totalSubscriptionPrice.value || 0;
    } catch (e) {
      // Surface the failure so the user sees something is wrong; the
      // cost-confirm carousel page will otherwise quietly show $0.
      console.error("[InviteTeammatePage] team.load() failed", describeError(e));
      if (this.$store && typeof this.$store.dispatch === "function") {
        this.$store.dispatch("raiseError", e);
      }
    }
    // Persistent open: the route IS the invite affordance, so the dialog
    // shows on mount and stays up until the user submits or leaves.
    if (this.$refs.inviteDialog && typeof this.$refs.inviteDialog.show === "function") {
      this.$refs.inviteDialog.show();
    }
    // Watch the dialog's internal showDialog so the X-close button
    // routes back to /team rather than stranding the user on a blank
    // /team/invite. The dialog's close() also nulls newTeammates, so
    // hasUnsavedInvitations will be false at this point -- no dirty
    // guard, just a clean return to the team page.
    this.$watch(
      () => !!this.$refs.inviteDialog && this.$refs.inviteDialog.showDialog,
      (open, wasOpen) => {
        if (wasOpen && !open && !this.inviteSent) {
          this.$router.push("/distributor/account/team");
        }
      }
    );
  },

  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedInvitations) {
      next();
      return;
    }
    this._pendingLeaveNext = next;
    this.unsavedDialog = true;
  },

  methods: {
    async onInviteSent(newTeammates) {
      // Once invites are sent (checkout cleared), close the sub-route by
      // routing back to the team page. The composable refresh happens
      // there on next mount; we just need to clear local state so the
      // guard does not fire on the redirect.
      this.inviteSent = true;
      try {
        await this.team.reloadSubscriptionData();
      } catch (e) {
        // Non-fatal: the team page will reload on mount.
      }
      this.$router.push("/distributor/account/team");
    },
    confirmLeaveUnsaved() {
      const next = this._pendingLeaveNext;
      this._pendingLeaveNext = null;
      this.unsavedDialog = false;
      if (next) next();
    },
    cancelLeaveUnsaved() {
      const next = this._pendingLeaveNext;
      this._pendingLeaveNext = null;
      this.unsavedDialog = false;
      if (next) next(false);
    },
  },
};
</script>

<style scoped>
.ip-unsaved-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 2400 to stack ABOVE Vuetify's v-dialog (default z-index 2000 +
     stacking strategy). Matches the StoreAddEdit unsaved-overlay
     pattern from PR #1329; the InviteTeammateDialog is teleported to
     <body> and stays mounted while this guard fires, so the overlay
     MUST out-stack it or the user gets stranded. */
  z-index: 2400;
}
.ip-unsaved-card {
  background: var(--tf-bg);
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: calc(100% - 32px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}
.ip-unsaved-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
}
.ip-unsaved-body {
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 20px;
}
.ip-unsaved-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.ip-unsaved-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 0.875rem;
  cursor: pointer;
}
.ip-unsaved-btn--ghost {
  background: var(--tf-surface-alt);
  color: var(--tf-fg-1);
}
.ip-unsaved-btn--danger {
  background: var(--tf-red);
  color: var(--tf-bg);
}
</style>
